import { Component, Input, OnInit } from '@angular/core';
import { IntegrityEdit } from '../model/integrity-edit';
import { GridViewManager } from 'src/app/core/model/grid-view-manager';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { ColumnReorderEvent, ColumnVisibilityChangeEvent, DataStateChangeEvent, GridComponent, GridDataResult } from '@progress/kendo-angular-grid';
import { State } from '@progress/kendo-data-query';
import { SVGIcon, checkCircleIcon, xOutlineIcon } from '@progress/kendo-svg-icons';
import { IntegrityService } from 'src/app/core/service/integrity.service';
import { AppConfigService } from 'src/app/core/service/app-config.service';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { TranslateService } from '@ngx-translate/core';
import { SettingService } from 'src/app/core/service/setting.service';
import { IntegrityItemAddressListService } from 'src/app/core/service/grid-data/grid-data-integrity-item-address-list.service';
import { GridStatePersistingService } from 'src/app/core/service/grid-state-persisting';
import { GridEventHandler, GridResizeEvent, GridSettings } from 'src/app/core/model/grid-settings';
import { GridQueryUtils } from 'src/app/core/utility/grid-query-utils';

@Component({
  selector: 'app-integrity-item-address-list',
  templateUrl: './integrity-item-address-list.component.html',
  styleUrls: ['./integrity-item-address-list.component.scss']
})
export class IntegrityItemAddressListComponent implements OnInit
{
  public integrityBatch: IntegrityEdit;
  public pageSizes = [5, 10, 25, 50, 100];
  public currentStage = 0;
  public integrityStages = [];
  public view: GridViewManager;
  public count: number;
  public enableAdvanced = false;
  public showStages = true;
  public selectedItems: number[] = [];
  public isLocked = false;
  public discarding = false;
  public formGroup: UntypedFormGroup;
  public gridView: GridDataResult;
  public patientLabelText = '';
  public state: State = {
    skip: 0,
    // Initial filter descriptor
    filter: {
      logic: 'and',
      filters: []
    },
    sort: []
  };

  public checkCircleIcon: SVGIcon = checkCircleIcon;
  public closeOutlineIcon: SVGIcon = xOutlineIcon;

  private editedRowIndex: number;
  private items: any[] = [];

  constructor(
    private integrityService: IntegrityService,
    private appConfigService: AppConfigService,
    private messageHandlerService: MessageHandlerService,
    private translateService: TranslateService,
    private settingService: SettingService,
    private integrityItemAddressListService: IntegrityItemAddressListService,
    private gridStatePersistingService: GridStatePersistingService)
  {
    this.view = new GridViewManager(this.appConfigService.getConfig().pageSize);
  }

  /**
   * Provide loading property from Service.
   */
  public get loading(): boolean
  {
    return this.integrityItemAddressListService ? this.integrityItemAddressListService.loading : false;
  }

  @Input() public set model(integrityEdit: IntegrityEdit)
  {
    this.initModel(integrityEdit);
  }

  public ngOnInit(): void
  {
    this.integrityStages = this.integrityService.integrityStages;
    this.integrityStages.forEach(x => { x.isValid = true; });
  }

  /**
   * Reorder grid column order event
   *
   * @param e ColumnReorderEvent event
   */
  public onReorder(e: ColumnReorderEvent): void
  {
    const reorderedColumn = this.view.columnsConfig.splice(e.oldIndex - 1, 1);
    this.view.columnsConfig.splice(e.newIndex - 1, 0, ...reorderedColumn);
    this.saveGrid();
  }

  /**
  * Grid onResize event.
  *
  * @param e Grid on resize column event.
  */
  public onResize(event: Array<GridResizeEvent>): void
  {
    event.forEach(item =>
    {
      const field = this.view.columnsConfig.find((col) => col.field === item.column.field);
      if (field !== undefined)
      {
        field.width = item.newWidth;
      }
    });
    this.saveGrid();
  }

  /**
   * Grid column visibility changed.
   *
   * @param event Grid column visibility event.
   */
  public onVisibilityChange(event: ColumnVisibilityChangeEvent): void
  {
    event.columns.forEach((column: any) =>
    {
      const field = this.view.columnsConfig.find((col) => col.field === column.field);
      if (field !== undefined)
      {
        field.hidden = column.hidden;
      }
    });
    this.saveGrid();
  }

  /**
   * Data change event handler.
   *
   * @param state State of grid operations
   */
  public dataStateChange(state: DataStateChangeEvent): void
  {
    this.state = state;
    this.saveGrid();
    this.executeQuery();
  }

  /**
   * Reset grid columns with default state
   */
  public resetGridColumns(): void
  {
    this.gridStatePersistingService.delete('grid-settings-integrity-address');
    this.initGridColumns();
    this.view.state.filter = { logic: 'and', filters: [] };
  }

  /**
   * Update the selected items and apply discarded updates to selected rows.
   */
  public updateItems(): void
  {
    if (this.selectedItems.length > 0)
    {
      this.discarding = true;
      // Prepare items to update
      this.items = [];
      this.selectedItems.forEach(item =>
      {
        const dataIndex = this.gridView.data.findIndex(x => x.id === item);
        if (dataIndex > -1)
        {
          this.items.push(
            {
              id: this.gridView.data[dataIndex].id,
              discarded: !this.gridView.data[dataIndex].discarded
            });
        }
      });

      // Update batch items
      this.integrityService.updateIntegrityBatchItemsAddress(this.items).subscribe({
        next: () =>
        {
          this.messageHandlerService.displaySuccess('BATCH-ITEMS-UPDATED-SUCCESSFULLY');
          this.selectedItems = [];
          this.executeQuery();
        },
        error: (errorMessage) =>
        {
          this.messageHandlerService.displayError(errorMessage);
          this.discarding = false;
        },
        complete: () =>
        {
          this.discarding = false;
        }
      });
    }
  }

  /**
   * Edit item handler
   *
   * @param event Grid event
   */
  public editHandler(event: GridEventHandler): void
  {
    this.closeEditor(event.sender);

    // Create the editable form group. Only discarded is allow editing.
    this.formGroup = new UntypedFormGroup(
      {
        id: new UntypedFormControl(event.dataItem.id),
        discarded: new UntypedFormControl(event.dataItem.discarded),
      }
    );
    this.editedRowIndex = event.rowIndex;
    event.sender.editRow(event.rowIndex, this.formGroup);
  }

  /**
   * Cancel edit handler
   */
  public cancelHandler(event: GridEventHandler): void
  {
    this.closeEditor(event.sender, event.rowIndex);
  }

  /**
   * Save item handler
   */
  public saveHandler(event: GridEventHandler): void
  {
    const item = event.formGroup.value;
    this.integrityService.updateIntegrityBatchItemAddress(item).subscribe({
      next: () =>
      {
        this.messageHandlerService.displaySuccess('BATCH-ITEM-UPDATED-SUCCESSFULLY');
        this.executeQuery();
      },
      error: (errorMessage) =>
      {
        this.messageHandlerService.displayError(errorMessage);
      }
    });
    event.sender.closeRow(event.rowIndex);
  }

  public onSelectedKeysChange(e: any): void
  {
    this.selectedItems = e;
  }

  public getColumnsForGroup(groupNumber: number): any
  {
    return this.view.columnsConfig.filter(x => x.groupNumber === groupNumber);
  }

  /**
   * Close editor handler
   *
   * @param grid Item grid
   * @param rowIndex Index on the selected row
   */
  private closeEditor(grid: GridComponent, rowIndex = this.editedRowIndex)
  {
    grid.closeRow(rowIndex);
    this.editedRowIndex = undefined;
    this.formGroup = undefined;
  }

  /**
   * Execute patient import list query
   */
  private executeQuery(): void
  {
    const httpPostBody = GridQueryUtils.normalizeFilters(this.state);
    this.integrityItemAddressListService.query((items) =>
    {
      items.data.forEach(d =>
      {
        d.processedDate = d.processedDate !== null ? new Date(d.processedDate) : null;
        d.lastModifiedOn = new Date(d.lastModifiedOn);
      });

      this.count = items.total;
      this.gridView = items;
    }, '', null, null, 'post', httpPostBody);
  }

  /**
   * Grid columns initialization
   */
  private initGridColumns(): void
  {

    this.translateService.get(['INTEGRITY-PATIENT-NAME', 'DATE-OF-BIRTH', 'SHORT-ADDRESS-LINE-1', 'SHORT-ADDRESS-LINE-2',
      'CITY', 'COUNTY', 'STATE', 'POSTAL-CODE', 'COUNTRY', 'INTEGRITY-PERSON-NAME', 'DISCARDED', 'IS-VALID', 'MESSAGES',
      'IMPORT-PROCESSED-DATE', 'CREATED-BY', 'CREATED-DATE', 'LAST-MODIFIED-BY', 'LAST-MODIFIED-DATE',
      'IS-REQUIRE-UPDATE', 'MEMBER-ID', 'IS-UPDATE'
    ]).subscribe({
      next: (res) =>
      {
        this.view.columnsConfig = [
          { field: 'id', title: res.ID, titleKeys: ['ID'], hidden: true, groupNumber: 0 },
          {
            field: 'memberId', title: res['MEMBER-ID'].replace('{{patientLabel}}', this.patientLabelText),
            titleKeys: ['MEMBER-ID'], hidden: true, groupNumber: 0
          },
          {
            field: 'memberName', title: res['INTEGRITY-PATIENT-NAME'].replace('{{patientLabel}}', this.patientLabelText),
            titleKeys: ['INTEGRITY-PATIENT-NAME'], hidden: false, width: 130, groupNumber: 0
          },
          { field: 'memberDateOfBirth', title: res['DATE-OF-BIRTH'], titleKeys: ['DATE-OF-BIRTH'], hidden: true, width: 90, groupNumber: 0 },
          {
            field: 'personName', title: res['INTEGRITY-PERSON-NAME'], titleKeys: ['INTEGRITY-PERSON-NAME'],
            hidden: this.integrityBatch.integrityType === 'ADDRESS_MEMBER', width: 130, groupNumber: 0
          },
          { field: 'street', title: res['SHORT-ADDRESS-LINE-1'], titleKeys: ['SHORT-ADDRESS-LINE-1'], hidden: false, width: 100, groupNumber: 1 },
          { field: 'streetAdditional', title: res['SHORT-ADDRESS-LINE-2'], titleKeys: ['SHORT-ADDRESS-LINE-2'], hidden: false, width: 75, groupNumber: 1 },
          { field: 'city', title: res.CITY, titleKeys: ['CITY'], hidden: false, width: 75, groupNumber: 1 },
          /*Google API currently not supporting county
          { field: 'county', title: res.COUNTY, titleKeys: ['COUNTY'], hidden: false, groupNumber: 1 },*/
          { field: 'state', title: res.STATE, titleKeys: ['STATE'], hidden: false, width: 75, groupNumber: 1 },
          { field: 'postalCode', title: res['POSTAL-CODE'], titleKeys: ['POSTAL-CODE'], hidden: false, width: 75, groupNumber: 1 },
          { field: 'country', title: res.COUNTRY, titleKeys: ['COUNTRY'], hidden: false, width: 75, groupNumber: 1 },
          { field: 'discarded', title: res.DISCARDED, titleKeys: ['DISCARDED'], filter: 'boolean', editor: 'boolean', hidden: false, width: 75, groupNumber: 0 },
          { field: 'valid', title: res['IS-VALID'], titleKeys: ['IS-VALID'], filter: 'boolean', hidden: false, width: 75, groupNumber: 0 },
          { field: 'requireUpdate', title: res['IS-REQUIRE-UPDATE'], titleKeys: ['IS-REQUIRE-UPDATE'], filter: 'boolean', hidden: false, width: 75, groupNumber: 0 },
          { field: 'errorMessage', title: res.MESSAGES, titleKeys: ['MESSAGES'], hidden: false, width: 120, groupNumber: 0 },
          {
            field: 'processedDate', title: res['IMPORT-PROCESSED-DATE'], titleKeys: ['IMPORT-PROCESSED-DATE'], hidden: true,
            format: '{0:g}', filter: 'date', groupNumber: 0
          },
          { field: 'createdBy', title: res['CREATED-BY'], titleKeys: ['CREATED-BY'], hidden: true, groupNumber: 0 },
          {
            field: 'createdOn', title: res['CREATED-DATE'], titleKeys: ['CREATED-DATE'], hidden: true,
            format: '{0:g}', filter: 'date', groupNumber: 0
          },
          { field: 'lastModifiedBy', title: res['LAST-MODIFIED-BY'], titleKeys: ['LAST-MODIFIED-BY'], hidden: true, groupNumber: 0 },
          {
            field: 'lastModifiedOn', title: res['LAST-MODIFIED-DATE'], titleKeys: ['LAST-MODIFIED-DATE'], hidden: true,
            format: '{0:g}', filter: 'date', groupNumber: 0
          },
        ];

        // If there is a previously saved grid settings, use it.
        const gridSettings: GridSettings = this.gridStatePersistingService.get('grid-settings-integrity-address');
        if (gridSettings !== null)
        {
          const loadedColumns = GridQueryUtils.setBaseColumnsSetting(this.translateService,
            JSON.stringify(gridSettings.columnsConfig), this.view.columnsConfig, this.patientLabelText);
          this.view.columnsConfig = loadedColumns;

          if (gridSettings.state && gridSettings.state.filter && gridSettings.state.filter.filters)
          {
            GridQueryUtils.convertFiltersToJSDates(gridSettings.state.filter.filters, this.view.columnsConfig);
          }
          this.view.state = gridSettings.state;
        }
      }
    });
  }

  /**
   * Persit grid columns into storage.
   */
  private saveGrid(): void
  {
    const gridConfig: GridSettings = { columnsConfig: this.view.columnsConfig, state: this.view.state };
    this.gridStatePersistingService.set('grid-settings-integrity-address', gridConfig);
  }

  private initModel(integrityEdit: IntegrityEdit): void
  {
    this.integrityBatch = integrityEdit;

    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting) =>
      {
        this.patientLabelText = sessionSetting.patientLabel;
        this.initGridColumns();

        // Load patient import batch id
        this.integrityService.getIntegrityBatchById(this.integrityBatch.id).subscribe({
          next: (data: IntegrityEdit) =>
          {
            this.selectedItems = [];
            this.isLocked = data.locked;

            // Set the current import stage
            const stageIndex = this.integrityStages.findIndex(x => x.value === data.integrityStage);
            this.currentStage = stageIndex > -1 ? stageIndex : 0;
            if (data.integrityStage === 'COMPLETED')
            {
              this.currentStage += 1;
            }

            // Set the validation error state and show to the VALIDATED
            if (data.integrityStage === 'VALIDATED' && data.validationErrors)
            {
              const stageValidatingIndex = this.integrityStages.findIndex(x => x.value === 'VALIDATING');
              this.integrityStages[stageValidatingIndex].isValid = false;
            }

            // Load grid data
            this.state = {
              filter: {
                filters: [
                  { field: 'integrityBatchId', operator: 'eq', value: this.integrityBatch.id }
                ], logic: 'and'
              }, group: [], skip: 0,
              sort: [
                { field: 'id', dir: 'asc' }
              ],
              take: this.appConfigService.getConfig().pageSize
            };
            this.executeQuery();
          },
          error: (errorResponse) =>
          {
            this.messageHandlerService.displayError(errorResponse);
          }
        });
      }
    });
  }
}